import { Heart, MessageCircle, Repeat2, TriangleAlert, Users } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UserCell } from '@/components/tombolo/UserCell';
import { actionLabel } from '@/lib/tombolo';
import type { TomboloState } from '@/hooks/useTombolo';

function StatBox({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg border bg-card p-4 text-center">
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export function StepParticipants({ state }: { state: TomboloState }) {
  const { engagement, setStep } = state;

  if (!engagement) return null;

  const { totals, participants, profiles, failedRelays, notesScanned } = engagement;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" aria-hidden />
          People found
        </CardTitle>
        <CardDescription className="text-base">
          Everyone who engaged with your notes, counted once per person —
          even if they liked, reposted and commented on several notes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {failedRelays.length > 0 && (
          <Alert>
            <TriangleAlert className="h-4 w-4" aria-hidden />
            <AlertTitle>Some relays didn't answer</AlertTitle>
            <AlertDescription>
              We continued with the working relays, but results from these could be missing:{' '}
              <span className="font-mono text-xs">{failedRelays.join(', ')}</span>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatBox label="Notes scanned" value={notesScanned} />
          <StatBox label="Likes found" value={totals.likes} />
          <StatBox label="Reposts found" value={totals.reposts} />
          <StatBox label="Comments found" value={totals.comments} />
          <StatBox label="Total events" value={totals.rawEvents} />
          <StatBox label="Unique people" value={participants.length} />
        </div>

        {participants.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="text-muted-foreground max-w-sm mx-auto">
                Nobody engaged with these notes yet — or the relays we asked
                don't have the data. Try different notes or check back later.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ScrollArea className="h-80 rounded-lg border">
            <ul className="divide-y">
              {participants.map((p) => (
                <li key={p.pubkey} className="flex items-center justify-between gap-3 p-3">
                  <UserCell pubkey={p.pubkey} metadata={profiles[p.pubkey]} />
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="hidden sm:flex items-center gap-1 text-muted-foreground" aria-hidden>
                      {p.actions.includes('like') && <Heart className="h-4 w-4" />}
                      {p.actions.includes('repost') && <Repeat2 className="h-4 w-4" />}
                      {p.actions.includes('comment') && <MessageCircle className="h-4 w-4" />}
                    </span>
                    <Badge variant="secondary">{actionLabel(p.actions)}</Badge>
                    {p.noteIds.length > 1 && (
                      <Badge variant="outline">{p.noteIds.length} notes</Badge>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </ScrollArea>
        )}

        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
          <Button variant="outline" onClick={() => setStep('rules')}>
            Back
          </Button>
          <Button size="lg" onClick={() => setStep('filter')} disabled={participants.length === 0}>
            Continue to bot filter
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
